import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 * Native port of the v1 `Messaging` factory — and byte-for-byte of its TWIN, the
 * v1 `Integration` factory: account-v1/src/core/api/touchpoint.js:20-31 registers
 * BOTH names with the IDENTICAL body `{ get: $http.get, put: $http.put }` (a bare
 * $http alias). WS5 #10 Stage A inc 2w replaces the `$injector.get('Messaging')`
 * grab in ft-touchpoint-messaging; the `$injector.get('Integration')` grab in
 * ft-touchpoint-integration drains onto this SAME class.
 *
 * The consumers call `put(url, changes)` FIRE-AND-FORGET (no .then) — v1 $http
 * fired the request on call; HttpClient observables are cold, so `firstValueFrom`
 * subscribes immediately to preserve that. The promise resolves with the response
 * BODY (no live consumer reads it; v1 resolved the $http envelope — documented
 * divergence, observable only to a consumer that doesn't exist).
 *
 * DOCUMENTED (oracle-blind, server-equivalent) divergences, per the 2w verify:
 * - Content-Type header: HttpClient sends `application/json` where $http sent
 *   `application/json;charset=utf-8` (applies to every drained native service).
 * - Error channel: a FAILED fire-and-forget put surfaces as an unhandled promise
 *   rejection (pageerror) where v1's unhandled $q rejection logged via
 *   console.error — error-path-only, both land in the console.
 */
@Injectable({ providedIn: 'root' })
export class MessagingV1ApiService {
  private readonly http = inject(HttpClient);

  /** v1 `Messaging.put` / `Integration.put` (= $http.put). */
  put(url: string, body: any): Promise<any> {
    return firstValueFrom(this.http.put(url, body));
  }

  /** v1 `Messaging.get` / `Integration.get` (= $http.get). */
  get(url: string): Promise<any> {
    return firstValueFrom(this.http.get(url));
  }
}
